import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Divider,
  Typography,
} from "@mui/material";
import {
  GridColDef,
  GridColumnGroupingModel,
  GridExpandMoreIcon,
} from "@mui/x-data-grid";
import { GKClientDataGrid, Grid2 } from "components";
import DashboardLayout from "layouts/DashboardLayout";
import {
  Navigate,
  createSearchParams,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { extraColumnsField } from "utils/helpers";

const formatValue = (value?: number | string | null) =>
  value === null || value === undefined || value === "" ? "-" : value;

const TRAILING_PERIODS = [
  { key: "1y", label: "1 yr" },
  { key: "3y", label: "3 yr" },
  { key: "5y", label: "5 yr" },
  { key: "10y", label: "10 yr" },
  { key: "sinceInception", label: "Since Inception" },
];

const RISK_RATIOS = [
  { key: "sortino", label: "Sortino" },
  { key: "informationRatio", label: "Information" },
  { key: "sharpe", label: "Sharpe" },
];

const RISK_HORIZONS = ["1", "3", "5"];

const SectionAccordion = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Accordion
    disableGutters
    style={{
      border: `solid 1px var(--neutral-100)`,
      borderRadius: "8px",
      boxShadow: "none",
      background: "none",
    }}
  >
    <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
      <Typography fontSize={16} fontWeight={600}>
        {title}
      </Typography>
    </AccordionSummary>
    <Divider />
    <AccordionDetails>{children}</AccordionDetails>
  </Accordion>
);

const nameColumn: GridColDef = {
  ...extraColumnsField("Scheme / Category Name", "name", 300, 2),
};

const MFReturns = () => {
  const navigate = useNavigate();
  // The MF page passes the category table response with only the selected funds
  const { state } = useLocation();
  const category: string = state?.category || "";
  const response = state?.response;
  const funds: any[] = response?.funds || [];
  const years: string[] = response?.meta?.years || [];
  const benchmarkLabel = response?.benchmark?.name || response?.meta?.benchmark;
  const benchmarkName = benchmarkLabel
    ? `Benchmark (${benchmarkLabel})`
    : "Benchmark";

  // Benchmark and category median share the fund shape (returns, rolling, risk)
  const summaries = [
    { id: "benchmark", name: benchmarkName, data: response?.benchmark },
    {
      id: "category-median",
      name: "Category Median",
      data: response?.categoryMedian,
    },
  ];

  // One row per fund, then benchmark and category median.
  // Spread first: some sections carry their own `name`, keep our labels
  const buildRows = (getValues: (item: any) => Record<string, any>) => [
    ...funds.map((fund) => ({
      ...getValues(fund),
      id: fund.schemeCode,
      name: fund.name,
    })),
    ...summaries.map((summary) => ({
      ...getValues(summary.data),
      id: summary.id,
      name: summary.name,
    })),
  ];

  /* ---------- 1. Trailing returns ---------- */
  const trailingRows = buildRows((item) => item?.returns);
  const trailingColumns: GridColDef[] = [
    nameColumn,
    ...TRAILING_PERIODS.map((period) => ({
      ...extraColumnsField(period.label, period.key, 110, 0),
      valueGetter: (row: any) => formatValue(row?.row?.[period.key]),
    })),
  ];

  /* ---------- 2. Calendar year returns ---------- */
  const calendarRows = buildRows((item) => item?.calendarReturns);
  const calendarColumns: GridColDef[] = [
    nameColumn,
    ...years.map((year) => ({
      ...extraColumnsField(year, year, 90, 0),
      valueGetter: (row: any) => formatValue(row?.row?.[year]),
    })),
  ];

  /* ---------- 3 & 4. Rolling returns ---------- */
  const bucketEdges: number[] = response?.meta?.bucketEdges || [];
  const bucketLabels = bucketEdges.length
    ? [
        `Less than ${bucketEdges[0]}%`,
        ...bucketEdges
          .slice(0, -1)
          .map((edge, index) => `${edge} - ${bucketEdges[index + 1]}%`),
        `Greater than ${bucketEdges[bucketEdges.length - 1]}%`,
      ]
    : [];

  const rollingStats = [
    { key: "average", label: "Average" },
    { key: "median", label: "Median" },
    { key: "maximum", label: "Maximum" },
    { key: "minimum", label: "Minimum" },
  ];

  const rollingColumns: GridColDef[] = [
    nameColumn,
    ...rollingStats.map((stat) => ({
      ...extraColumnsField(stat.label, stat.key, 100, 0),
      valueGetter: (row: any) => formatValue(row?.row?.[stat.key]),
    })),
    ...bucketLabels.map((label) => ({
      ...extraColumnsField(label, label, 110, 0),
      valueGetter: (row: any) => formatValue(row?.row?.distribution?.[label]),
    })),
  ];

  const rollingGrouping: GridColumnGroupingModel = [
    {
      groupId: "Return Statistics (%)",
      children: rollingStats.map((stat) => ({ field: stat.key })),
    },
    {
      groupId: "Return distribution (% of times)",
      children: bucketLabels.map((label) => ({ field: label })),
    },
  ];

  const getRollingRows = (horizon: string) =>
    buildRows((item) => item?.rolling?.[horizon]);

  // Opened directly or after a refresh there is no data, go back to MF
  if (!response) return <Navigate to="/app/mf" replace />;

  /* ---------- 5. Risk ratios ---------- */
  // risk.<ratio> comes keyed by horizon in years: { "1": .., "3": .., "5": .. }
  const ratioColumns: GridColDef[] = [
    nameColumn,
    ...RISK_RATIOS.flatMap((ratio) =>
      RISK_HORIZONS.map((horizon) => ({
        ...extraColumnsField(
          `${ratio.label} ${horizon}y`,
          `${ratio.key}-${horizon}`,
          120,
          0
        ),
        valueGetter: (row: any) =>
          formatValue(row?.row?.risk?.[ratio.key]?.[horizon]),
      }))
    ),
  ];
  const ratioRows = buildRows((item) => ({ risk: item?.risk }));

  /* ---------- 6. Others ---------- */
  const otherFields = [
    { key: "maxDrawdown", label: "Max Drawdown (%)" },
    { key: "recoveryWeeks", label: "Recovery Period (Weeks)" },
    { key: "downsideCapture", label: "Downside Captured" },
    { key: "upsideCapture", label: "Upside Captured" },
    { key: "pe", label: "P/E" },
    { key: "pb", label: "P/B" },
  ];
  const otherColumns: GridColDef[] = [
    nameColumn,
    ...otherFields.map((field) => ({
      ...extraColumnsField(field.label, field.key, 130, 0),
      valueGetter: (row: any) => formatValue(row?.row?.[field.key]),
    })),
  ];
  // Funds and benchmark nest these under drawdown/capture, category median has them flat
  const otherRows = buildRows((item) => ({
    maxDrawdown:
      item?.risk?.drawdown?.maxDrawdownPct ?? item?.risk?.maxDrawdownPct,
    recoveryWeeks:
      item?.risk?.drawdown?.recoveryWeeks ?? item?.risk?.recoveryWeeks,
    downsideCapture:
      item?.risk?.capture?.downsideCapture ?? item?.risk?.downsideCapture,
    upsideCapture:
      item?.risk?.capture?.upsideCapture ?? item?.risk?.upsideCapture,
    pe: item?.pe,
    pb: item?.pb,
  }));

  const tableProps = {
    showHeaderGrid: false,
    pagination: false,
    hideFooter: true,
    disableRowSelectionOnClick: true,
  };

  return (
    <DashboardLayout title="MF">
      <Grid2 container spacing={2}>
        <Grid2
          size={12}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography fontSize={16} fontWeight={600}>
            {category}
          </Typography>
          <Button
            variant="outlined"
            onClick={() =>
              navigate({
                pathname: "/app/mf",
                search: createSearchParams({ category }).toString(),
              })
            }
          >
            Back
          </Button>
        </Grid2>
        <Grid2 size={12}>
          <SectionAccordion title="Trailing Returns">
            <GKClientDataGrid
              {...tableProps}
              columns={trailingColumns}
              rows={trailingRows}
            />
          </SectionAccordion>
        </Grid2>
        <Grid2 size={12}>
          <SectionAccordion title="Calendar Year Returns">
            <GKClientDataGrid
              {...tableProps}
              columns={calendarColumns}
              rows={calendarRows}
            />
          </SectionAccordion>
        </Grid2>
        <Grid2 size={12}>
          <SectionAccordion title="Rolling Return - 3 yr">
            <GKClientDataGrid
              {...tableProps}
              columns={rollingColumns}
              rows={getRollingRows("3")}
              columnGroupingModel={rollingGrouping}
            />
          </SectionAccordion>
        </Grid2>
        <Grid2 size={12}>
          <SectionAccordion title="Rolling Return - 5 yr">
            <GKClientDataGrid
              {...tableProps}
              columns={rollingColumns}
              rows={getRollingRows("5")}
              columnGroupingModel={rollingGrouping}
            />
          </SectionAccordion>
        </Grid2>
        <Grid2 size={12}>
          <SectionAccordion title="Sortino / Information / Sharpe">
            <GKClientDataGrid
              {...tableProps}
              columns={ratioColumns}
              rows={ratioRows}
            />
          </SectionAccordion>
        </Grid2>
        <Grid2 size={12}>
          <SectionAccordion title="Others Returns">
            <GKClientDataGrid
              {...tableProps}
              columns={otherColumns}
              rows={otherRows}
            />
          </SectionAccordion>
        </Grid2>
      </Grid2>
    </DashboardLayout>
  );
};

export default MFReturns;
